import { TicketStatus, Priority } from '../../types/tickets';

// Helper functions to convert enum numbers to strings and handle comparisons
export const getStatusString = (status: number | string): string => {
  if (typeof status === 'string') return status;
  
  switch (status) {
    case TicketStatus.Open: return 'Open';
    case TicketStatus.InProgress: return 'InProgress';
    case TicketStatus.Pending: return 'Pending';
    case TicketStatus.Resolved: return 'Resolved';
    case TicketStatus.Closed: return 'Closed';
    case TicketStatus.OnHold: return 'OnHold';
    default: return 'Unknown';
  }
};

export const getPriorityString = (priority: number | string): string => {
  if (typeof priority === 'string') return priority;
  
  switch (priority) {
    case Priority.Low: return 'Low';
    case Priority.Medium: return 'Medium';
    case Priority.High: return 'High';
    case Priority.Critical: return 'Critical';
    default: return 'Unknown';
  }
};

export const getStatusBadgeVariant = (status: number | string) => {
  const statusStr = getStatusString(status).toLowerCase();
  switch (statusStr) {
    case 'open': return 'destructive';
    case 'inprogress': return 'default';
    case 'pending': return 'default';
    case 'resolved': return 'secondary';
    case 'closed': return 'outline';
    case 'onhold': return 'outline';
    default: return 'default';
  }
};

export const getPriorityBadgeVariant = (priority: number | string) => {
  const priorityStr = getPriorityString(priority).toLowerCase();
  switch (priorityStr) {
    case 'high': return 'destructive';
    case 'critical': return 'destructive';
    case 'medium': return 'default';
    case 'low': return 'secondary';
    default: return 'outline';
  }
};

export const isTicketOpen = (status: number | string): boolean => {
  if (typeof status === 'string') {
    return status.toLowerCase() === 'open' || status.toLowerCase() === 'inprogress';
  }
  return status === TicketStatus.Open || status === TicketStatus.InProgress;
};

export const isTicketResolved = (status: number | string): boolean => {
  if (typeof status === 'string') {
    return status.toLowerCase() === 'resolved';
  }
  return status === TicketStatus.Resolved;
};

export const isTicketClosed = (status: number | string): boolean => {
  if (typeof status === 'string') {
    return status.toLowerCase() === 'closed';
  }
  return status === TicketStatus.Closed;
};

export const getStatusIcon = (status: number | string) => {
  const statusStr = getStatusString(status).toLowerCase();
  switch (statusStr) {
    case 'open': return '🔴';
    case 'inprogress': return '🟡';
    case 'pending': return '🟠';
    case 'resolved': return '🟢';
    case 'closed': return '⚪';
    case 'onhold': return '⏸️';
    default: return '🔵';
  }
};

// Get status icon name for lucide-react icons
export const getStatusIconName = (status: number | string): string => {
  const statusStr = getStatusString(status).toLowerCase();
  switch (statusStr) {
    case 'open': return 'Circle';
    case 'inprogress': return 'Clock';
    case 'pending': return 'Hourglass';
    case 'resolved': return 'CheckCircle';
    case 'closed': return 'XCircle';
    case 'onhold': return 'PauseCircle';
    default: return 'Circle';
  }
};

// Get priority icon name for lucide-react icons
export const getPriorityIconName = (priority: number | string): string => {
  const priorityStr = getPriorityString(priority).toLowerCase();
  switch (priorityStr) {
    case 'low': return 'ArrowDown';
    case 'medium': return 'Minus';
    case 'high': return 'ArrowUp';
    case 'critical': return 'AlertTriangle';
    default: return 'Minus';
  }
};

// Get status accent bar color (for left border)
export const getStatusAccentColor = (status: number | string): string => {
  const statusStr = getStatusString(status).toLowerCase();
  switch (statusStr) {
    case 'open': return 'border-l-blue-500';
    case 'inprogress': return 'border-l-yellow-500';
    case 'pending': return 'border-l-orange-500';
    case 'resolved': return 'border-l-green-500';
    case 'closed': return 'border-l-gray-500';
    case 'onhold': return 'border-l-purple-500';
    default: return 'border-l-gray-300';
  }
};

// Get priority background tint
export const getPriorityBackgroundTint = (priority: number | string): string => {
  const priorityStr = getPriorityString(priority).toLowerCase();
  switch (priorityStr) {
    case 'low': return 'bg-gray-50/50 dark:bg-gray-900/20';
    case 'medium': return 'bg-blue-50/30 dark:bg-blue-900/10';
    case 'high': return 'bg-orange-50/40 dark:bg-orange-900/15';
    case 'critical': return 'bg-red-50/50 dark:bg-red-900/20';
    default: return '';
  }
};